import {
  AttachMoney,
  AutoAwesome,
  Category,
  Close,
  HomeOutlined,
  OpenInNew,
  Search,
  Sort,
  Storefront,
  Tune,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { logFiltro, logM } from 'utils/observability';
import { registrarCliqueProduto } from 'services/produtosAfiliados';
import { PerfilPresente } from 'config/perfisPresentes';
import { trackClick, usePageView } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { trackGoogleAdsConversion } from 'utils/googleTag';
import {
  CATEGORIAS_GRUPOS,
  FAIXAS_PRECO,
  PERFIL_OFERTAS_DIA,
  PERFIL_OFERTAS_SEMANA,
  PERFIL_TODOS,
  useIdeiasPresentesRules,
} from './IdeiasPresentesPage.rules';

const formatarPreco = (valor: number) =>
  valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type SecaoFiltro = 'categoria' | 'catgrupo' | 'loja' | 'faixa';

const IdeiasPresentesPage: React.FC = () => {
  usePageView('IdeiasPresentes');

  const brandTheme = BUILD_PROFILE.theme;
  const {
    perfis,
    temOfertasDia,
    temOfertasSemana,
    afiliados,
    fontesSelecionadas,
    perfilSelecionado,
    produtos,
    total,
    carregando,
    erro,
    temMais,
    faixaSlug,
    catGrupo,
    ordem,
    setOrdem,
    busca,
    setBusca,
    carregarMais,
    selecionarPerfil,
    aplicarFiltros,
  } = useIdeiasPresentesRules();

  // Ordem fixa dos chips de categoria: Todos, Ofertas do dia/semana e o resto.
  const chipsPerfis = (() => {
    const lista: PerfilPresente[] = [];
    const add = (p?: PerfilPresente | null) => {
      if (p && !lista.some((x) => x.slug === p.slug)) lista.push(p);
    };
    add(PERFIL_TODOS);
    if (temOfertasDia) add(PERFIL_OFERTAS_DIA);
    if (temOfertasSemana) add(PERFIL_OFERTAS_SEMANA);
    perfis.forEach(add);
    return lista;
  })();

  // "Lojas": todas on; clica p/ remover ([] = todas, caminho seguro).
  const todasFontes = afiliados.map((a) => a.chave);
  const lojaAtiva = (chave: string) =>
    fontesSelecionadas.length === 0 || fontesSelecionadas.includes(chave);
  const toggleLoja = (chave: string) => {
    const atual = fontesSelecionadas.length === 0 ? todasFontes : fontesSelecionadas;
    let novo = atual.includes(chave) ? atual.filter((c) => c !== chave) : [...atual, chave];
    // [] = todas (caminho seguro: evita mandar a lista cheia e cobre o "removeu tudo")
    if (novo.length === 0 || novo.length === todasFontes.length) novo = [];
    aplicarFiltros({
      perfilSlug: perfilSelecionado?.slug ?? 'todos',
      fontes: novo,
      faixaSlug,
      catGrupo,
    });
  };

  // Menu de ordenação (botão ao lado do buscar)
  const [ordenarAnchor, setOrdenarAnchor] = useState<null | HTMLElement>(null);
  const OPCOES_ORDEM = [
    { valor: 'relevancia', label: 'Relevância' },
    { valor: 'preco-asc', label: 'Menor preço' },
    { valor: 'preco-desc', label: 'Maior preço' },
  ];

  // ----- Painel de filtros (drawer) com estado em rascunho, aplicado no "Aplicar" -----
  // secaoAtiva = qual filtro está aberto (categoria | loja | faixa); null = fechado
  const [secaoAtiva, setSecaoAtiva] = useState<SecaoFiltro | null>(null);
  const [draftPerfil, setDraftPerfil] = useState<string>('todos');
  const [draftFontes, setDraftFontes] = useState<string[]>([]);
  const [draftFaixa, setDraftFaixa] = useState<string>('todas');
  const [draftCatGrupo, setDraftCatGrupo] = useState<string>('');

  const abrirSecao = (secao: SecaoFiltro) => {
    // Só semeia os rascunhos ao abrir do zero; ao trocar de seção, preserva o que já foi mexido
    if (secaoAtiva === null) {
      setDraftPerfil(perfilSelecionado?.slug ?? 'todos');
      setDraftFontes(fontesSelecionadas);
      setDraftFaixa(faixaSlug);
      setDraftCatGrupo(catGrupo);
    }
    setSecaoAtiva(secao);
    trackClick('ideias_filtros_abrir', { secao });
  };

  const fecharSecao = () => setSecaoAtiva(null);

  const alternarDraftFonte = (chave: string) =>
    setDraftFontes((atuais) =>
      atuais.includes(chave) ? atuais.filter((c) => c !== chave) : [...atuais, chave]
    );

  // "Limpar" zera só a seção aberta (o painel mostra um filtro por vez)
  const limparSecao = () => {
    if (secaoAtiva === 'categoria') setDraftPerfil('todos');
    else if (secaoAtiva === 'catgrupo') setDraftCatGrupo('');
    else if (secaoAtiva === 'loja') setDraftFontes([]);
    else if (secaoAtiva === 'faixa') setDraftFaixa('todas');
  };

  const aplicar = () => {
    aplicarFiltros({
      perfilSlug: draftPerfil,
      fontes: draftFontes,
      faixaSlug: draftFaixa,
      catGrupo: draftCatGrupo,
    });
    setSecaoAtiva(null);
    // Log de uso dos filtros (Sentry, message 'filtro') — 1 por dimensão ativa, tipo-valor
    if (draftPerfil && draftPerfil !== 'todos') logFiltro(`perfil-${draftPerfil}`);
    if (draftCatGrupo) logFiltro(`categoria-${draftCatGrupo}`);
    draftFontes.forEach((fonte) => logFiltro(`loja-${fonte}`));
    if (draftFaixa && draftFaixa !== 'todas') logFiltro(`preco-${draftFaixa}`);
    trackClick('ideias_filtros_aplicar', {
      perfil: draftPerfil,
      fontes: draftFontes.join(',') || 'todas',
      faixa: draftFaixa,
      categoria: draftCatGrupo || 'todas',
    });
  };

  // Grupo de categoria ativo (usado no rótulo do FAB de categoria)
  const grupoAtivo = CATEGORIAS_GRUPOS.find((g) => g.slug === catGrupo);

  // Conversão Google Ads "Ver oferta" — clicar em qualquer produto conta como conversão.
  // Só dispara quando a label estiver preenchida no buildConfig (ação criada no Google Ads).
  const dispararConversaoOferta = (produtoId: string | number, preco: number) => {
    logM('Oferta', preco);
    registrarCliqueProduto(String(produtoId), 'oferta');
    const label = BUILD_PROFILE.googleAdsViewOfferConversionLabel;
    if (label) {
      trackGoogleAdsConversion(BUILD_PROFILE.googleAdsId, label, `oferta-${produtoId}`);
    }
  };

  // Carrega mais ao chegar perto do fim da página (infinite scroll)
  const sentinelaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelaRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) carregarMais();
      },
      { rootMargin: '700px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [carregarMais, perfilSelecionado]);

  // Card de perfil usado na landing (sem perfil selecionado)
  const renderPerfilCard = (perfil: PerfilPresente, onSelect: () => void) => (
    <Card
      key={perfil.slug}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brandTheme.palette.borderSoft}`,
        background: `linear-gradient(150deg, rgba(255,255,255,0.94) 0%, ${brandTheme.palette.heroGradientEnd} 130%)`,
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 12px 24px ${brandTheme.palette.heroGlow}`,
        },
      }}
    >
      <CardActionArea
        onClick={onSelect}
        sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', minHeight: { xs: 104, md: 128 } }}
      >
        <Typography sx={{ fontSize: { xs: '1.9rem', md: '2.3rem' }, lineHeight: 1 }}>
          {perfil.emoji}
        </Typography>
        <Typography sx={{ fontWeight: 700, mt: 0.75, fontSize: { xs: '0.92rem', md: '1rem' } }}>
          {perfil.label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.25 }}
        >
          {perfil.descricao}
        </Typography>
      </CardActionArea>
    </Card>
  );

  // ----- Conteúdo (chips) de cada seção — o título/subtítulo fica no cabeçalho do painel -----
  const conteudoCategoria = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {chipsPerfis.map((perfil) => {
        const ativo = perfil.slug === draftPerfil;
        return (
          <Chip
            key={perfil.slug}
            label={`${perfil.emoji} ${perfil.label}`}
            onClick={() => setDraftPerfil(perfil.slug)}
            color={ativo ? 'primary' : 'default'}
            variant={ativo ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: 999 }}
          />
        );
      })}
    </Box>
  );

  const conteudoLoja = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Chip
        icon={<AutoAwesome sx={{ fontSize: 18 }} />}
        label="Todas as lojas"
        onClick={() => setDraftFontes([])}
        color={draftFontes.length === 0 ? 'primary' : 'default'}
        variant={draftFontes.length === 0 ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600, borderRadius: 999 }}
      />
      {afiliados.map((afiliado) => {
        const ativo = draftFontes.includes(afiliado.chave);
        return (
          <Chip
            key={afiliado.chave}
            avatar={
              <Avatar
                variant="rounded"
                src={afiliado.logo}
                alt={afiliado.label}
                sx={{
                  bgcolor: afiliado.cor,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  '& img': { objectFit: 'cover' },
                }}
              >
                {afiliado.label.charAt(0)}
              </Avatar>
            }
            label={afiliado.label}
            onClick={() => alternarDraftFonte(afiliado.chave)}
            color={ativo ? 'primary' : 'default'}
            variant={ativo ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: 999 }}
          />
        );
      })}
    </Box>
  );

  const conteudoFaixa = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {FAIXAS_PRECO.map((faixa) => {
        const ativo = faixa.slug === draftFaixa;
        return (
          <Chip
            key={faixa.slug}
            label={faixa.label}
            onClick={() => setDraftFaixa(faixa.slug)}
            color={ativo ? 'secondary' : 'default'}
            variant={ativo ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: 999 }}
          />
        );
      })}
    </Box>
  );

  const conteudoCatGrupo = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Chip
        icon={<AutoAwesome sx={{ fontSize: 18 }} />}
        label="Todas"
        onClick={() => setDraftCatGrupo('')}
        color={draftCatGrupo === '' ? 'primary' : 'default'}
        variant={draftCatGrupo === '' ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600, borderRadius: 999 }}
      />
      {CATEGORIAS_GRUPOS.map((grupo) => {
        const ativo = grupo.slug === draftCatGrupo;
        return (
          <Chip
            key={grupo.slug}
            label={`${grupo.emoji} ${grupo.label}`}
            onClick={() => setDraftCatGrupo(grupo.slug)}
            color={ativo ? 'primary' : 'default'}
            variant={ativo ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: 999 }}
          />
        );
      })}
    </Box>
  );

  const SECOES_INFO: Record<
    SecaoFiltro,
    { titulo: string; subtitulo: string; conteudo: React.ReactNode }
  > = {
    categoria: {
      titulo: 'Perfil',
      subtitulo: 'Para quem é o presente?',
      conteudo: conteudoCategoria,
    },
    catgrupo: {
      titulo: 'Categoria',
      subtitulo: 'Tipo de produto',
      conteudo: conteudoCatGrupo,
    },
    loja: { titulo: 'Loja', subtitulo: 'Onde comprar', conteudo: conteudoLoja },
    faixa: {
      titulo: 'Faixa de preço',
      subtitulo: 'Quanto pretende gastar?',
      conteudo: conteudoFaixa,
    },
  };

  // ----- Painel flutuante (modalzinho) à esquerda dos FABs; eles seguem visíveis/clicáveis -----
  const renderPainelFiltros = () => {
    const info = secaoAtiva ? SECOES_INFO[secaoAtiva] : null;
    if (!info) return null;
    return (
      <>
        {/* Camada que fecha ao tocar fora — fica ATRÁS dos FABs (que continuam clicáveis p/ trocar) */}
        <Box
          onClick={fecharSecao}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (t) => t.zIndex.speedDial - 1,
            backgroundColor: { xs: 'rgba(17,24,39,0.28)', sm: 'transparent' },
          }}
        />
        {/* Modalzinho */}
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 32 },
            right: { xs: 80, md: 88 },
            width: { xs: 'calc(100vw - 96px)', sm: 360 },
            maxWidth: 360,
            maxHeight: { xs: '62vh', md: 'calc(100vh - 150px)' },
            zIndex: (t) => t.zIndex.speedDial,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: brandTheme.palette.surface,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            borderRadius: 3,
            boxShadow: '0 24px 60px rgba(17,24,39,0.28)',
            overflow: 'hidden',
          }}
        >
          {/* Cabeçalho */}
          <Box
            sx={{
              px: 2,
              pt: 1.75,
              pb: 1.25,
              borderBottom: `1px solid ${brandTheme.palette.borderSoft}`,
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                  {info.titulo}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {info.subtitulo}
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Fechar filtros" onClick={fecharSecao}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Conteúdo rolável */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.75 }}>{info.conteudo}</Box>

          {/* Rodapé */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${brandTheme.palette.borderSoft}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              backgroundColor: brandTheme.palette.surface,
            }}
          >
            <Button
              onClick={limparSecao}
              size="small"
              startIcon={<Close />}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Limpar
            </Button>
            <Button
              variant="contained"
              onClick={aplicar}
              sx={{ borderRadius: 999, fontWeight: 800, textTransform: 'none', px: 2.5 }}
            >
              Aplicar
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={1.5}
      width="100%"
      sx={{ mt: { xs: -2.5, md: -1.5 } }}
    >
      {!perfilSelecionado ? (
        // ----- Seleção de perfil (landing) -----
        <>
          <Box textAlign="center" maxWidth={680} px={1}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: '1.45rem', md: '2.1rem' }, fontWeight: 800, lineHeight: 1.1 }}
            >
              💡 Ideias de Presentes
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: { xs: '0.92rem', md: '1.02rem' } }}
            >
              Não sabe o que dar? Escolha para quem é o presente e a gente te inspira.
            </Typography>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }}
            gap={{ xs: 1.25, md: 1.75 }}
            width="100%"
            maxWidth={920}
          >
            {perfis.map((perfil) =>
              renderPerfilCard(perfil, () => {
                trackClick('ideias_perfil_card', { perfil: perfil.slug });
                selecionarPerfil(perfil.slug);
              })
            )}
          </Box>
        </>
      ) : (
        // ----- Lista de produtos do perfil -----
        <Box width="100%" maxWidth={1060} display="flex" flexDirection="column" gap={1.75}>
          {/* Cabeçalho: casinha (home) à esquerda + título centralizado (sem header) */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title="Início">
              <IconButton
                component={RouterLink}
                to="/"
                aria-label="Início"
                size="small"
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: brandTheme.palette.textPrimary,
                }}
              >
                <HomeOutlined />
              </IconButton>
            </Tooltip>
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.3rem', md: '1.6rem' },
                lineHeight: 1.1,
                textAlign: 'center',
              }}
            >
              💡 Ideias de Presentes
            </Typography>
          </Box>

          {/* Filtros principais: "Para quem" (1 coluna, inclui Ofertas dia/semana, nomes truncam)
              + "Lojas" (logo, todas on) — lado a lado. As 5 lojas cabem sem scroll; o scroll fica
              só no "Para quem". Sem títulos de seção (em teste). */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <Box
              sx={{ border: `1px solid ${brandTheme.palette.borderSoft}`, borderRadius: 2, p: 1.25 }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 0.75,
                  maxHeight: { xs: 196, md: 220 },
                  overflowY: 'auto',
                }}
              >
                {chipsPerfis.map((perfil) => {
                  const ativo = perfilSelecionado?.slug === perfil.slug;
                  return (
                    <Chip
                      key={perfil.slug}
                      label={`${perfil.emoji} ${perfil.label}`}
                      onClick={() => selecionarPerfil(perfil.slug)}
                      color={ativo ? 'primary' : 'default'}
                      variant={ativo ? 'filled' : 'outlined'}
                      sx={{
                        width: '100%',
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        fontWeight: 600,
                        borderRadius: 999,
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{ border: `1px solid ${brandTheme.palette.borderSoft}`, borderRadius: 2, p: 1.25 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  maxHeight: { xs: 196, md: 220 },
                  overflowY: 'auto',
                }}
              >
                {afiliados.map((loja) => {
                  const ativa = lojaAtiva(loja.chave);
                  return (
                    <Box
                      key={loja.chave}
                      role="button"
                      onClick={() => toggleLoja(loja.chave)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 0.75,
                        py: 0.4,
                        borderRadius: 999,
                        cursor: 'pointer',
                        border: `1px solid ${ativa ? brandTheme.palette.primaryMain : brandTheme.palette.borderSoft}`,
                        bgcolor: ativa ? brandTheme.palette.heroGradientEnd : 'transparent',
                        opacity: ativa ? 1 : 0.5,
                        transition: 'opacity 120ms ease',
                      }}
                    >
                      <Avatar
                        src={loja.logo}
                        alt={loja.label}
                        sx={{
                          width: 22,
                          height: 22,
                          bgcolor: loja.cor,
                          fontSize: 12,
                          '& img': { objectFit: 'contain' },
                        }}
                      >
                        {loja.label.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                        {loja.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: brandTheme.palette.borderSoft }} />

          {erro && <Alert severity="warning">{erro}</Alert>}

          {!erro && !carregando && produtos.length === 0 && (
            <Alert severity="info">
              Ainda não temos sugestões nessa combinação. Toque em <strong>Filtros</strong> e
              experimente outra faixa de preço. 😉
            </Alert>
          )}

          {/* Contagem + filtro por texto livre na mesma linha */}
          <Stack direction="row" spacing={1} alignItems="center">
            {total > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 600 }}
              >
                {total} {total === 1 ? 'item' : 'itens'}
              </Typography>
            )}
            <TextField
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por palavra"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: busca ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Limpar busca" onClick={() => setBusca('')}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            />
            <Tooltip title="Ordenar">
              <IconButton
                size="small"
                aria-label="Ordenar"
                onClick={(e) => setOrdenarAnchor(e.currentTarget)}
                sx={{
                  flexShrink: 0,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  borderRadius: 2,
                }}
              >
                <Sort fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Menu
            anchorEl={ordenarAnchor}
            open={Boolean(ordenarAnchor)}
            onClose={() => setOrdenarAnchor(null)}
          >
            {OPCOES_ORDEM.map((opt) => (
              <MenuItem
                key={opt.valor}
                selected={ordem === opt.valor}
                onClick={() => {
                  setOrdem(opt.valor);
                  setOrdenarAnchor(null);
                  trackClick('ideias_ordenar', { ordem: opt.valor });
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Grid de produtos */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={{ xs: 1.25, md: 1.75 }}
          >
            {produtos.map((produto) => (
              <Card
                key={produto.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(255,255,255,0.92)',
                }}
              >
                <Box
                  component="a"
                  href={produto.urlAfiliado}
                  target="_blank"
                  rel="sponsored nofollow noopener"
                  onClick={() => {
                    trackClick('ideias_produto_click', {
                      produtoId: produto.id,
                      perfil: perfilSelecionado.slug,
                      parceiro: produto.parceiro ?? undefined,
                    });
                    dispararConversaoOferta(produto.id, produto.preco);
                  }}
                  sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      backgroundColor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                      borderRadius: '8px',
                    }}
                  >
                    {produto.imagemUrl ? (
                      <Box
                        component="img"
                        src={produto.imagemUrl}
                        alt={produto.nome}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '2.5rem' }}>🎁</Typography>
                    )}
                    {produto.precoOriginal && produto.precoOriginal > produto.preco && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          left: 6,
                          bgcolor: '#2e7d32',
                          color: '#fff',
                          borderRadius: 1.5,
                          px: 0.75,
                          py: 0.2,
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          lineHeight: 1.4,
                        }}
                      >
                        -{Math.round((1 - produto.preco / produto.precoOriginal) * 100)}%
                      </Box>
                    )}
                  </Box>
                  <CardContent
                    sx={{
                      p: { xs: 1.25, md: 1.5 },
                      // MUI força pb:24px no :last-child — sobrescreve p/ não sobrar abaixo dos textos
                      '&:last-child': { pb: { xs: 1.25, md: 1.5 } },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.82rem', md: '0.9rem' },
                        fontWeight: 600,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: { xs: '2.15rem', md: '2.35rem' },
                      }}
                    >
                      {produto.nome}
                    </Typography>
                    {produto.precoOriginal && produto.precoOriginal > produto.preco && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through', display: 'block', mt: 0.5, lineHeight: 1 }}
                      >
                        {formatarPreco(produto.precoOriginal)}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: brandTheme.palette.primaryMain,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mt: produto.precoOriginal && produto.precoOriginal > produto.preco ? 0 : 0.5,
                      }}
                    >
                      {formatarPreco(produto.preco)}
                    </Typography>
                    {produto.parceiro && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        em {produto.parceiro}
                      </Typography>
                    )}
                  </CardContent>
                </Box>
                <Box px={{ xs: 1.25, md: 1.5 }} pb={{ xs: 1.25, md: 1.5 }} mt="auto">
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    endIcon={<OpenInNew sx={{ fontSize: 15 }} />}
                    component="a"
                    href={produto.urlAfiliado}
                    target="_blank"
                    rel="sponsored nofollow noopener"
                    onClick={() => {
                      trackClick('ideias_produto_cta', {
                        produtoId: produto.id,
                        perfil: perfilSelecionado.slug,
                        parceiro: produto.parceiro ?? undefined,
                      });
                      dispararConversaoOferta(produto.id, produto.preco);
                    }}
                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                  >
                    Ver oferta
                  </Button>
                </Box>
              </Card>
            ))}

            {carregando &&
              Array.from({ length: produtos.length === 0 ? 8 : 4 }).map((_, index) => (
                <Card
                  key={`skeleton-${index}`}
                  elevation={0}
                  sx={{ borderRadius: 3, border: `1px solid ${brandTheme.palette.borderSoft}` }}
                >
                  <Skeleton variant="rectangular" sx={{ aspectRatio: '1 / 1', width: '100%', height: 'auto' }} />
                  <Box p={1.5}>
                    <Skeleton width="90%" />
                    <Skeleton width="55%" />
                  </Box>
                </Card>
              ))}
          </Box>

          {/* Sentinela do infinite scroll */}
          {temMais && <Box ref={sentinelaRef} sx={{ height: 1 }} />}
          {carregando && produtos.length > 0 && (
            <CircularProgress size={28} sx={{ alignSelf: 'center' }} />
          )}

          {/* Fim da lista: convida a explorar outra categoria via painel de filtros */}
          {!temMais && !carregando && produtos.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 2.5,
                borderTop: `1px dashed ${brandTheme.palette.borderSoft}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                🎉 Chegamos ao fim das ideias de {perfilSelecionado.emoji} {perfilSelecionado.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quer ver presentes para outra pessoa? Ajuste os filtros.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => abrirSecao('categoria')}
                startIcon={<Tune />}
                sx={{ borderRadius: 999, fontWeight: 700, textTransform: 'none', px: 2.5 }}
              >
                Trocar categoria
              </Button>
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.4, px: 0.25, mt: 1, opacity: 0.75 }}
          >
            Preços e disponibilidade são atualizados diariamente e podem variar — o valor final é
            sempre o exibido no site do parceiro ao clicar.
          </Typography>

          {/* Botões flutuantes de filtro — um por dimensão (categoria, loja, preço) */}
          <Stack
            spacing={1.25}
            sx={{
              position: 'fixed',
              bottom: { xs: 24, md: 32 },
              right: { xs: 16, md: 28 },
              zIndex: (t) => t.zIndex.speedDial,
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                px: 0.75,
                py: 0.2,
                borderRadius: 1,
                bgcolor: '#fff',
                border: `1px solid ${brandTheme.palette.borderSoft}`,
                boxShadow: '0 4px 10px rgba(17,24,39,0.12)',
                fontSize: '0.6rem',
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: brandTheme.palette.textPrimary,
                lineHeight: 1.4,
              }}
            >
              Filtros
            </Box>
            {(
              [
                {
                  secao: 'categoria',
                  label: 'Perfil',
                  icone: (
                    <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>
                      {perfilSelecionado?.emoji ?? '🎁'}
                    </Box>
                  ),
                },
                {
                  secao: 'catgrupo',
                  label: 'Categoria',
                  icone: grupoAtivo ? (
                    <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>
                      {grupoAtivo.emoji}
                    </Box>
                  ) : (
                    <Category />
                  ),
                },
                { secao: 'loja', label: 'Loja', icone: <Storefront /> },
                { secao: 'faixa', label: 'Faixa de preço', icone: <AttachMoney /> },
              ] as { secao: SecaoFiltro; label: string; icone: React.ReactNode }[]
            ).map(({ secao, label, icone }) => {
              const ativoBtn = secao === secaoAtiva;
              return (
                <Tooltip key={secao} title={label} placement="left">
                  <Fab
                    size="medium"
                    onClick={() => abrirSecao(secao)}
                    aria-label={label}
                    sx={{
                      bgcolor: ativoBtn ? brandTheme.palette.textPrimary : '#fff',
                      color: ativoBtn ? '#fff' : brandTheme.palette.textPrimary,
                      border: `1px solid ${brandTheme.palette.borderSoft}`,
                      boxShadow: '0 8px 20px rgba(17,24,39,0.18)',
                      '&:hover': {
                        bgcolor: ativoBtn ? brandTheme.palette.textPrimary : '#f4f4f6',
                      },
                    }}
                  >
                    {icone}
                  </Fab>
                </Tooltip>
              );
            })}
            <Tooltip title="Ordenar" placement="left">
              <Fab
                size="medium"
                onClick={(e) => setOrdenarAnchor(e.currentTarget)}
                aria-label="Ordenar"
                sx={{
                  bgcolor: ordem !== 'relevancia' ? brandTheme.palette.textPrimary : '#fff',
                  color: ordem !== 'relevancia' ? '#fff' : brandTheme.palette.textPrimary,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  boxShadow: '0 8px 20px rgba(17,24,39,0.18)',
                  '&:hover': {
                    bgcolor: ordem !== 'relevancia' ? brandTheme.palette.textPrimary : '#f4f4f6',
                  },
                }}
              >
                <Sort />
              </Fab>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {renderPainelFiltros()}
    </Box>
  );
};

export default IdeiasPresentesPage;
